/* @flow */
import * as React from 'react';

import { Box, Button, Flex, Footer, Icon, Space, Table, Text } from '@sonyged/proc-ui';
import css from '@styled-system/css';
import styled from 'styled-components';

import { HintButton, PizzaButton } from './buttons';
import { HintModal } from './HintModal';
import { Pizza } from '../../../pizza/fields/domain/pizza';
import { PizzaIngredientIcon } from '../../../pizza/fields/icons/PizzaIngredientIcon';
import { YesterdayPizzaModal } from './YesterdayPizzaModal';
import { crustOptions, sauceOptions, toppingOptions } from './options';
import ProcPizzaSelect from '../../../pizza/fields/ProcPizzaSelect';

type Props = {
  yesterdayPizza: Pizza,
  yesterdaySale: number,
  yesterdayStar: number,
  onNext: Pizza => void,
  onBack: Function,
};

export function PizzaBuilderView(props: Props) {
  let { yesterdayPizza, yesterdaySale, yesterdayStar, onNext, onBack } = props;

  let [crust, setCrust] = React.useState(null);
  let [sauce, setSauce] = React.useState(null);
  let [toppings, setToppings] = React.useState([null, null, null]);

  let [modal, setModal] = React.useState<'yesterday' | 'hint' | null>(null);

  let setToppingAt = React.useCallback((index, topping) => {
    setToppings(toppings => {
      let newToppings = toppings.slice(0);
      newToppings[index] = topping;
      return newToppings;
    });
  }, []);

  let nullableIngredients = [crust, sauce].concat(toppings);
  let ingredients = nullableIngredients.filter(Boolean);
  let done = ingredients.length === 5;

  let firstInput = React.useRef();
  React.useLayoutEffect(() => {
    if (firstInput.current) {
      firstInput.current.focus();
    }
  }, []);

  return (
    <Page>
      <Info>
        <Space mx={['7px', '7px', '15px']}>
          <PizzaButton pizza={yesterdayPizza} onClick={() => setModal('yesterday')}>
            昨日のピザ
          </PizzaButton>
          <HintButton onClick={() => setModal('hint')}>調べる</HintButton>
        </Space>
      </Info>

      <Main className="pizza-builder-view">
        <Space my={[0, 0, 48]}>
          <SettingPanel>
            <Space mt={[24, 24, 36]}>
              <Text color="gray100" fontSize={[18, 18, 22]} lineHeight={1}>
                ピザをつくろう
              </Text>
            </Space>
            <Space my={[12, 12, 24]}>
              <Text color="gray100" fontSize={[14, 14, 17]} lineHeight={1}>
                「調べる」を見て本日のピザの材料を選ぼう
              </Text>
            </Space>

            <Space mb={[16, 16, 32]}>
              <IngredientList className="ingredient-icons">
                <Space mx={['3px']}>
                  {nullableIngredients.map((ingredient, i) => {
                    return <PizzaIngredientIcon ingredient={ingredient} key={i} />;
                  })}
                </Space>
              </IngredientList>

              <SettingTable>
                <thead>
                  <tr>
                    <th width={'50%'}>ピザきじ</th>
                    <th width={'50%'}>ソース</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={'value'}>
                      <ProcPizzaSelect
                        innerRef={firstInput}
                        tabIndex={0}
                        value={crust}
                        options={crustOptions}
                        onSelect={setCrust}
                        blankLabel="選ぶ"
                      />
                    </td>
                    <td className={'value'}>
                      <ProcPizzaSelect
                        tabIndex={0}
                        value={sauce}
                        options={sauceOptions}
                        onSelect={setSauce}
                        blankLabel="選ぶ"
                      />
                    </td>
                  </tr>
                </tbody>
              </SettingTable>

              <SettingTable>
                <thead>
                  <tr>
                    <th colSpan={2}>トッピング</th>
                  </tr>
                </thead>
                <tbody>
                  <React.Fragment>
                    <tr>
                      <td width="50%">
                        <ProcPizzaSelect
                          blankLabel="選ぶ"
                          tabIndex={0}
                          value={toppings[0]}
                          options={toppingOptions}
                          onSelect={topping => setToppingAt(0, topping)}
                        />
                      </td>
                      <td width="50%">
                        <ProcPizzaSelect
                          blankLabel="選ぶ"
                          tabIndex={0}
                          value={toppings[1]}
                          options={toppingOptions}
                          onSelect={topping => setToppingAt(1, topping)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <ProcPizzaSelect
                          blankLabel="選ぶ"
                          tabIndex={0}
                          value={toppings[2]}
                          options={toppingOptions}
                          onSelect={topping => setToppingAt(2, topping)}
                        />
                      </td>
                      <td />
                    </tr>
                  </React.Fragment>
                </tbody>
              </SettingTable>
            </Space>
          </SettingPanel>
        </Space>
      </Main>

      <Footer variant="raised">
        <Footer.ButtonGroup>
          <Button onClick={onBack}>
            <Icon variant="back" />
          </Button>
        </Footer.ButtonGroup>
        <Footer.ActionButton
          disabled={!done}
          onClick={() => crust && sauce && onNext(new Pizza(crust, sauce, toppings.filter(Boolean)))}
        >
          <Icon variant="action" />
        </Footer.ActionButton>
      </Footer>

      <YesterdayPizzaModal
        isOpen={modal === 'yesterday'}
        pizza={yesterdayPizza}
        sale={yesterdaySale}
        star={yesterdayStar}
        onClose={() => setModal(null)}
      />
      <HintModal isOpen={modal === 'hint'} pizza={yesterdayPizza} onClose={() => setModal(null)} />
    </Page>
  );
}

const Page = styled(Box)(
  css({
    display: 'flex',
    flexDirection: 'column',
    size: '100%',
  })
);

const Info = styled(Box)(
  css({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 106,
    pb: [24],
    bg: 'tertiary100',
  })
);

const Main = styled(Box)(
  css({
    flex: 1,
    minHeight: 0,
    bg: 'gray10',
    overflow: 'auto',
  })
);

const IngredientList = styled(Flex)(
  css({
    justifyContent: 'center',
    alignSelf: 'stretch',
    '&&': {
      margin: 0,
      mb: [16, 16, 32],
    },
  })
);

const SettingPanel = styled.div(
  css({
    display: 'flex',
    borderRadius: 15,
    mx: 'auto',
    maxWidth: [440, 440, 800],
    width: [null, null, 800],
    bg: [null, null, 'white'],
    flexDirection: 'column',
    alignItems: 'center',
    px: [16, 16, 32],
  })
);

const SettingTable = styled(Table).attrs({ borderRadius: [8, 8, 12] })(
  css({
    width: '100%',
    tableLayout: 'fixed',

    'th, td': {
      px: [12, 12, 16],
      fontSize: ['mobile.small', 'smallPC.small', 20],
      lineHeight: 'title',
    },

    'thead th': {
      py: [6],
      color: 'secondary100',
      textAlign: 'left',
      fontWeight: 'normal',
    },

    'tbody td': {
      color: 'gray100',
      bg: 'white',
      py: [10, 10, 12],
    },
  })
);
